// Light, dark or the system's (docs/brand.md: both themes on every page). The choice is a
// per-browser convenience, so it lives in localStorage and the page works without it.
export type Theme = 'system' | 'light' | 'dark'
const KEY = 'apsio.theme'

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

/**
 * A theme switch is instant: for two frames the page carries data-theme-switching, under which
 * nothing transitions (components.css), so colors never pass through a mix of both themes, which
 * reads as a flash and, mid-way, under AA (the console e2e's axe caught it on a switch).
 */
function quietSwitch(): void {
  const root = document.documentElement
  root.setAttribute('data-theme-switching', '')
  requestAnimationFrame(() =>
    requestAnimationFrame(() => root.removeAttribute('data-theme-switching')),
  )
}
let watching = false
function watchSystemTheme(): void {
  if (watching || typeof matchMedia !== 'function') return
  watching = true
  matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', quietSwitch)
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  watchSystemTheme()
  const now = root.getAttribute('data-theme') ?? 'system'
  if (now !== theme) quietSwitch()
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function saveTheme(theme: Theme): void {
  applyTheme(theme)
  try {
    if (theme === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, theme)
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
}
